// @flow
import FieldValidator from "common/records/FieldValidator"
import * as validators from "common/services/utils/validators"

export const PRESETS = {
  NAME: "name",
  NAME_CYRILLIC: "name_cyrillic",
  LATIN: "latin",
  BID: "bid",
  DATE: "date",
  PHONE: "phone",
  INTERNATIONAL_PHONE: "international_phone",
  DECIMAL: "decimal",
  ADULT: "adult",
  EMAIL: "email",
  DOCUMENT_EXPIRATION: "document",
  DOCUMENT_NUMBER: "idNumber",
  BIRTHDAY: "birthday",
  SWIFT: "swift",
  IBAN: "iban",
  SAME_EMAIL: "same_email",
  REASON: "reason",
}

const DATE_FORMAT = ["YYYY-MM-DD", "YYYY-M-D"]

// BAD DESIGN DECISION
// Fields are required in certain cases.
// This makes it awkward to check with Flow

type Options = {
  required?: boolean,
  minDate?: string,
  dateAlternative?: string,
  adultThreshold?: number,
  strict?: boolean,
  email?: string,
}

export const COMMON_ERRORS = {
  IS_REQUIRED: __("forms.errors.is_required"),
  NOT_LATIN: __("forms.errors.not_latin"),
  TOO_SHORT: __("forms.too_short"),
  NOT_CYRILLIC: __("forms.errors.not_cyrillic"),
}

export const PHONE_ERRORS = {
  INVALID_PHONE: __("forms.errors.invalid_phone"),
}

export const DECIMAL_ERRORS = {
  INVALID_DECIMAL: __("forms.errors.invalid_decimal"),
}

export const NAME_ERRORS = {
  NAME_TOO_SHORT: __("forms.errors.name_too_short"),
}

export const BID_ERRORS = {
  INVALID_BID: __("forms.errors.invalid_bid"),
}

export const BIRTHDAY_ERRORS = {
  INVALID_BIRTHDAY: __("forms.errors.invalid_birthday"),
  NOT_ADULT: __("forms.errors.not_adult"),
}

export const DATE_ERRORS = {
  INVALID_DATE: __("forms.errors.invalid_date"),
  DATE_TOO_EARLY: __("forms.errors.date_too_early"),
}

export const EMAIL_ERRORS = {
  INVALID_EMAIL: __("forms.wrong_format_email"),
  EMAIL_ALREADY_SET: __("forms.errors.email_already_set"),
}

export const SWIFT_ERRORS = {
  INVALID_SWIFT: __("forms.errors.invalid_swift"),
}

export const IBAN_ERRORS = {
  INVALID_IBAN: __("forms.errors.invalid_iban"),
}

export const ID_NUMBER_ERRORS = {
  INVALID_ID_NUMBER: __("forms.too_short"),
  ID_NUMBER_TOO_LONG: __("forms.id_number_too_long"),
}

function isTextFilled(input: ?string): boolean {
  return input ? input.length > 0 : false
}

function isReasonLongEnough(input: ?string): boolean {
  return input ? input.length > 20 : false
}

export function getValidator(preset: ?string, origOptions: Options = {}): FieldValidator {
  const options = origOptions
  if (options.required !== false) {
    options.required = true
  }

  let validator = new FieldValidator({
    required: options.required,
  })

  if (options.required) {
    validator = validator.add(isTextFilled, COMMON_ERRORS.IS_REQUIRED)
  }

  switch (preset) {
    case PRESETS.NAME:
      return validator
        .add(validators.isName, NAME_ERRORS.NAME_TOO_SHORT)
        .add(validators.isLatin, COMMON_ERRORS.NOT_LATIN)

    case PRESETS.NAME_CYRILLIC:
      return validator
        .add(validators.isName, NAME_ERRORS.NAME_TOO_SHORT)
        .add(validators.isCyrillic, COMMON_ERRORS.NOT_CYRILLIC)

    case PRESETS.LATIN:
      return validator.add(validators.isWeakLatin, COMMON_ERRORS.NOT_LATIN)

    case PRESETS.DATE:
      return validator.add(validators.isDate, DATE_ERRORS.INVALID_DATE)

    case PRESETS.BID:
      return validator.add(val => validators.isBookingId(Number(val)), BID_ERRORS.INVALID_BID)

    case PRESETS.BIRTHDAY:
      return validator.add(
        value => validators.isBirthday(value, DATE_FORMAT, options.strict),
        BIRTHDAY_ERRORS.INVALID_BIRTHDAY,
      )

    case PRESETS.DOCUMENT_EXPIRATION:
      return validator
        .add(
          value =>
            validators.isDateWithAlternatives(
              value,
              DATE_FORMAT,
              options.strict,
              // $FlowFixMe
              options.dateAlternative,
            ),
          DATE_ERRORS.INVALID_DATE,
        )
        .add(
          value =>
            validators.isDocumentAfter(
              value,
              // $FlowFixMe
              options.minDate,
              options.dateAlternative,
              DATE_FORMAT,
              options.strict,
            ),
          DATE_ERRORS.DATE_TOO_EARLY,
        )

    case PRESETS.ADULT:
      return validator.add(validators.isBirthday, BIRTHDAY_ERRORS.INVALID_BIRTHDAY).add(
        value =>
          validators.isAdult(
            value,
            DATE_FORMAT,
            // $FlowFixMe
            options.adultThreshold,
          ),
        BIRTHDAY_ERRORS.NOT_ADULT,
      )

    case PRESETS.PHONE:
      return validator.add(validators.isPhone, PHONE_ERRORS.INVALID_PHONE)

    case PRESETS.INTERNATIONAL_PHONE:
      return validator.add(validators.isInternationalPhone, PHONE_ERRORS.INVALID_PHONE)

    case PRESETS.DECIMAL:
      return validator.add(validators.isDecimal, DECIMAL_ERRORS.INVALID_DECIMAL)

    case PRESETS.EMAIL:
      return validator.add(validators.isEmail, EMAIL_ERRORS.INVALID_EMAIL)

    case PRESETS.SWIFT:
      return validator.add(validators.isSwift, SWIFT_ERRORS.INVALID_SWIFT)

    case PRESETS.IBAN:
      return validator.add(validators.isIBAN, IBAN_ERRORS.INVALID_IBAN)

    case PRESETS.DOCUMENT_NUMBER:
      return validator
        .add(validators.isIdNumber, ID_NUMBER_ERRORS.INVALID_ID_NUMBER)
        .add(validators.isNotTooLong, ID_NUMBER_ERRORS.ID_NUMBER_TOO_LONG)

    case PRESETS.SAME_EMAIL:
      return validator
        .add(validators.isEmail, EMAIL_ERRORS.INVALID_EMAIL)
        .add(value => value !== options.email, EMAIL_ERRORS.EMAIL_ALREADY_SET)

    case PRESETS.REASON:
      return validator.add(isReasonLongEnough, COMMON_ERRORS.TOO_SHORT)

    default:
      return validator
  }
}

export const plainValidator = getValidator()
export const nameValidator = getValidator(PRESETS.NAME)
export const latinValidator = getValidator(PRESETS.LATIN)
export const bidValidator = getValidator(PRESETS.BID)
export const dateValidator = getValidator(PRESETS.DATE)
export const birthdayValidator = getValidator(PRESETS.BIRTHDAY)
export const phoneValidator = getValidator(PRESETS.PHONE)
export const internationalPhoneValidator = getValidator(PRESETS.INTERNATIONAL_PHONE)
export const decimalNumberValidator = getValidator(PRESETS.DECIMAL)
export const emailValidator = getValidator(PRESETS.EMAIL)
export const swiftValidator = getValidator(PRESETS.SWIFT)
export const ibanValidator = getValidator(PRESETS.IBAN)
export const idNumberValidator = getValidator(PRESETS.DOCUMENT_NUMBER)
export const sameEmailValidator = getValidator(PRESETS.SAME_EMAIL)
export const reasonValidator = getValidator(PRESETS.REASON)
